import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Avatar, Empty, Muted, PageHeader, Section, Tabs } from '@/components/ui'
import {
  createColaborador,
  fetchCargos,
  fetchColaborador,
  fetchDepartamentos,
  updateColaborador,
  type ColaboradorWritePayload,
} from './api'
import {
  formatMoneyInput,
  maskCpfInput,
  maskPhoneInput,
  onlyDigits,
  parseMoney,
} from './utils'

type FormState = {
  nome: string
  cpf: string
  salario_base: string
  data_admissao: string
  email_corporativo: string
  email_profissional: string
  email: string
  matricula: string
  regime: string
  status: string
  cargo: string
  departamento: string
  rg: string
  data_nascimento: string
  sexo: string
  estado_civil: string
  nacionalidade: string
  telefone: string
  celular: string
  cep: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cidade: string
  estado: string
  banco: string
  agencia: string
  conta: string
  tipo_conta: string
  pix_tipo: string
  pix_chave: string
  pis_pasep: string
  ctps_numero: string
  ctps_serie: string
  ctps_uf: string
  carga_horaria: string
  observacoes: string
}

type TabId = 'essenciais' | 'contato' | 'documentos' | 'endereco' | 'bancario' | 'observacoes'

const emptyForm = (): FormState => ({
  nome: '',
  cpf: '',
  salario_base: '',
  data_admissao: new Date().toISOString().slice(0, 10),
  email_corporativo: '',
  email_profissional: '',
  email: '',
  matricula: '',
  regime: 'clt',
  status: 'ativo',
  cargo: '',
  departamento: '',
  rg: '',
  data_nascimento: '',
  sexo: '',
  estado_civil: '',
  nacionalidade: 'Brasileiro(a)',
  telefone: '',
  celular: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  banco: '',
  agencia: '',
  conta: '',
  tipo_conta: '',
  pix_tipo: '',
  pix_chave: '',
  pis_pasep: '',
  ctps_numero: '',
  ctps_serie: '',
  ctps_uf: '',
  carga_horaria: '220',
  observacoes: '',
})

const ENDERECO_KEYS: (keyof FormState)[] = [
  'cep',
  'logradouro',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  'estado',
]

const BANCARIO_KEYS: (keyof FormState)[] = [
  'banco',
  'agencia',
  'conta',
  'tipo_conta',
  'pix_tipo',
  'pix_chave',
]

function hasAny(f: FormState, keys: (keyof FormState)[]) {
  return keys.some((k) => String(f[k] ?? '').trim() !== '')
}

function snapshotKey(f: FormState) {
  return JSON.stringify(f)
}

function toPayload(f: FormState): ColaboradorWritePayload {
  const salario = parseMoney(f.salario_base)
  return {
    nome: f.nome.trim(),
    cpf: onlyDigits(f.cpf),
    salario_base: salario,
    data_admissao: f.data_admissao,
    email_corporativo: f.email_corporativo.trim() || undefined,
    email_profissional: f.email_profissional.trim() || undefined,
    email: f.email.trim() || undefined,
    matricula: f.matricula.trim() || undefined,
    regime: f.regime || 'clt',
    status: f.status || 'ativo',
    cargo: f.cargo || null,
    departamento: f.departamento || null,
    rg: f.rg.trim() || undefined,
    data_nascimento: f.data_nascimento || null,
    sexo: f.sexo || undefined,
    estado_civil: f.estado_civil || undefined,
    nacionalidade: f.nacionalidade.trim() || undefined,
    telefone: f.telefone.trim() || undefined,
    celular: f.celular.trim() || undefined,
    cep: f.cep.trim() || undefined,
    logradouro: f.logradouro.trim() || undefined,
    numero: f.numero.trim() || undefined,
    complemento: f.complemento.trim() || undefined,
    bairro: f.bairro.trim() || undefined,
    cidade: f.cidade.trim() || undefined,
    estado: f.estado.trim().toUpperCase().slice(0, 2) || undefined,
    banco: f.banco.trim() || undefined,
    agencia: f.agencia.trim() || undefined,
    conta: f.conta.trim() || undefined,
    tipo_conta: f.tipo_conta.trim() || undefined,
    pix_tipo: f.pix_tipo.trim() || undefined,
    pix_chave: f.pix_chave.trim() || undefined,
    pis_pasep: f.pis_pasep.trim() || undefined,
    ctps_numero: f.ctps_numero.trim() || undefined,
    ctps_serie: f.ctps_serie.trim() || undefined,
    ctps_uf: f.ctps_uf.trim().toUpperCase().slice(0, 2) || undefined,
    carga_horaria: f.carga_horaria ? Number(f.carga_horaria) : 220,
    observacoes: f.observacoes.trim() || undefined,
  }
}

function Field({
  label,
  children,
  required,
  hint,
  className,
}: {
  label: string
  children: ReactNode
  required?: boolean
  hint?: string
  className?: string
}) {
  return (
    <label className={className}>
      <span>
        {label}
        {required ? ' *' : ''}
      </span>
      {children}
      {hint ? <em className="hi-field-hint">{hint}</em> : null}
    </label>
  )
}

function PanelEmpty({
  message,
  cta,
  onReveal,
}: {
  message: string
  cta: string
  onReveal: () => void
}) {
  return (
    <div className="hi-form-empty">
      <Empty>{message}</Empty>
      <button type="button" className="btn btn-secondary" onClick={onReveal}>
        {cta}
      </button>
    </div>
  )
}

export function ColaboradorFormPage() {
  const { colaboradorId } = useParams()
  const isEdit = Boolean(colaboradorId)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { canEdit } = useAuth()
  const [form, setForm] = useState<FormState>(emptyForm)
  const [baseline, setBaseline] = useState(() => snapshotKey(emptyForm()))
  const [tab, setTab] = useState<TabId>('essenciais')
  const [erro, setErro] = useState('')
  const [sucesso, setSucesso] = useState<{ id: string; nome: string } | null>(null)
  const [enderecoOpen, setEnderecoOpen] = useState(false)
  const [bancarioOpen, setBancarioOpen] = useState(false)
  const [obsOpen, setObsOpen] = useState(false)
  const baselineReady = useRef(false)

  const allowed = canEdit('colaboradores')
  const backTo = isEdit && colaboradorId ? `/app/rh/${colaboradorId}` : '/app/rh'
  const displayName = form.nome.trim() || (isEdit ? 'Colaborador' : 'Novo colaborador')
  const dirty = snapshotKey(form) !== baseline
  const enderecoEmpty = !hasAny(form, ENDERECO_KEYS)
  const bancarioEmpty = !hasAny(form, BANCARIO_KEYS)
  const showEndereco = enderecoOpen || !enderecoEmpty
  const showBancario = bancarioOpen || !bancarioEmpty
  const showObs = obsOpen || form.observacoes.trim() !== ''

  const existing = useQuery({
    queryKey: ['rh', 'colaborador', colaboradorId],
    queryFn: () => fetchColaborador(colaboradorId!),
    enabled: isEdit && Boolean(colaboradorId) && allowed,
  })

  const catalogs = useQuery({
    queryKey: ['rh', 'colaborador-form-catalogs'],
    queryFn: async () => {
      const [cargos, departamentos] = await Promise.all([
        fetchCargos({ completo: true }),
        fetchDepartamentos(),
      ])
      return {
        cargos: cargos.dados || [],
        departamentos,
      }
    },
    enabled: allowed,
  })

  useEffect(() => {
    if (isEdit) return
    if (baselineReady.current) return
    baselineReady.current = true
    const initial = emptyForm()
    setForm(initial)
    setBaseline(snapshotKey(initial))
  }, [isEdit])

  useEffect(() => {
    if (!existing.data) return
    const c = existing.data
    const next: FormState = {
      nome: c.nome || '',
      cpf: maskCpfInput(c.cpf || ''),
      salario_base:
        c.salario_base != null && c.salario_base !== ''
          ? formatMoneyInput(c.salario_base)
          : '',
      data_admissao: (c.data_admissao || '').slice(0, 10),
      email_corporativo: c.email_corporativo || '',
      email_profissional: c.email_profissional || '',
      email: (c.email as string) || '',
      matricula: c.matricula || '',
      regime: c.regime || 'clt',
      status: c.status || 'ativo',
      cargo: c.cargo_id ? String(c.cargo_id) : '',
      departamento: c.departamento_id ? String(c.departamento_id) : '',
      rg: (c.rg as string) || '',
      data_nascimento: (c.data_nascimento || '').slice(0, 10),
      sexo: (c.sexo as string) || '',
      estado_civil: (c.estado_civil as string) || '',
      nacionalidade: (c.nacionalidade as string) || 'Brasileiro(a)',
      telefone: maskPhoneInput((c.telefone as string) || ''),
      celular: maskPhoneInput((c.celular as string) || ''),
      cep: (c.cep as string) || '',
      logradouro: (c.logradouro as string) || '',
      numero: (c.numero as string) || '',
      complemento: (c.complemento as string) || '',
      bairro: (c.bairro as string) || '',
      cidade: (c.cidade as string) || '',
      estado: (c.estado as string) || '',
      banco: (c.banco as string) || '',
      agencia: (c.agencia as string) || '',
      conta: (c.conta as string) || '',
      tipo_conta: (c.tipo_conta as string) || '',
      pix_tipo: (c.pix_tipo as string) || '',
      pix_chave: (c.pix_chave as string) || '',
      pis_pasep: (c.pis_pasep as string) || '',
      ctps_numero: (c.ctps_numero as string) || '',
      ctps_serie: (c.ctps_serie as string) || '',
      ctps_uf: (c.ctps_uf as string) || '',
      carga_horaria: c.carga_horaria != null ? String(c.carga_horaria) : '220',
      observacoes: (c.observacoes as string) || '',
    }
    setForm(next)
    setBaseline(snapshotKey(next))
    setEnderecoOpen(hasAny(next, ENDERECO_KEYS))
    setBancarioOpen(hasAny(next, BANCARIO_KEYS))
    setObsOpen(next.observacoes.trim() !== '')
    setSucesso(null)
    setErro('')
    baselineReady.current = true
  }, [existing.data])

  const save = useMutation({
    mutationFn: async () => {
      const payload = toPayload(form)
      if (isEdit && colaboradorId) {
        return updateColaborador(colaboradorId, payload)
      }
      return createColaborador(payload)
    },
    onSuccess: async (dados) => {
      setErro('')
      await qc.invalidateQueries({ queryKey: ['rh'] })
      if (!isEdit) {
        navigate(`/app/rh/${dados.id}`)
        return
      }
      const nextBaseline = snapshotKey(form)
      setBaseline(nextBaseline)
      setSucesso({ id: String(dados.id), nome: dados.nome || form.nome })
    },
    onError: (err) => {
      setSucesso(null)
      setErro(err instanceof ApiError ? err.message : 'Falha ao salvar.')
    },
  })

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
    if (sucesso) setSucesso(null)
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.nome.trim() || onlyDigits(form.cpf).length !== 11 || !form.data_admissao) {
      setErro('Preencha nome, CPF (11 dígitos) e data de admissão.')
      setTab('essenciais')
      return
    }
    if (!form.salario_base || Number.isNaN(parseMoney(form.salario_base))) {
      setErro('Informe um salário base válido.')
      setTab('essenciais')
      return
    }
    save.mutate()
  }

  function onCancel() {
    if (dirty && !confirm('Há alterações não salvas. Deseja sair sem salvar?')) return
    navigate(backTo)
  }

  const tabItems: { id: TabId; label: string }[] = [
    { id: 'essenciais', label: 'Essenciais' },
    { id: 'contato', label: 'Contato' },
    { id: 'documentos', label: 'Documentos' },
    {
      id: 'endereco',
      label: enderecoEmpty ? 'Endereço · incompleto' : 'Endereço',
    },
    {
      id: 'bancario',
      label: bancarioEmpty ? 'Bancário · incompleto' : 'Bancário',
    },
    { id: 'observacoes', label: 'Observações' },
  ]

  if (!allowed) {
    return (
      <section className="hi-page">
        <Alert tone="danger">Sem permissão para cadastrar ou editar colaboradores.</Alert>
        <Link className="btn btn-secondary" to="/app/rh">
          Voltar
        </Link>
      </section>
    )
  }

  if (isEdit && existing.isLoading) return <Muted>Carregando…</Muted>
  if (isEdit && existing.isError) {
    return (
      <section className="hi-page">
        <Alert tone="danger">
          {existing.error instanceof ApiError ? existing.error.message : 'Não encontrado.'}
        </Alert>
        <Link className="btn btn-secondary" to="/app/rh">
          Voltar
        </Link>
      </section>
    )
  }

  const cargoNome =
    (catalogs.data?.cargos || []).find((c) => String(c.id) === form.cargo)?.nome || 'Sem cargo'
  const deptoNome =
    (catalogs.data?.departamentos || []).find((d) => String(d.id) === form.departamento)?.nome ||
    ''
  const primaryDisabled = save.isPending || (isEdit && !dirty)

  return (
    <section className="hi-page hi-form-page">
      <PageHeader
        title={isEdit ? 'Editar colaborador' : 'Novo colaborador'}
        subtitle="Cadastro via API (RH+). Para o fluxo completo use Admissão em Departamento Pessoal."
        backTo={{ to: backTo, label: 'Voltar' }}
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/colaboradores')}>
            Legado
          </a>
        }
      />

      {erro ? <Alert tone="danger">{erro}</Alert> : null}
      {sucesso ? (
        <Alert tone="success">
          Alterações salvas.{' '}
          <Link className="hi-link" to={`/app/rh/${sucesso.id}`}>
            Ver ficha
          </Link>
        </Alert>
      ) : null}

      <form className="hi-form-compose" onSubmit={onSubmit} noValidate>
        <div className="card hi-profile-head hi-form-intro">
          <Avatar nome={displayName} size="lg" />
          <div className="hi-form-intro-text">
            <p className="hi-form-kicker">{isEdit ? 'Edição' : 'Admissão rápida'}</p>
            <h4>{displayName}</h4>
            <p>
              {cargoNome}
              {deptoNome ? ` · ${deptoNome}` : ''}
              {form.matricula.trim() ? ` · Mat. ${form.matricula.trim()}` : ''}
            </p>
          </div>
        </div>

        <Tabs items={tabItems} value={tab} onChange={setTab} label="Seções do cadastro" />

        <div
          className="hi-form-panel"
          hidden={tab !== 'essenciais'}
          id="panel-essenciais"
          role="tabpanel"
        >
          <Section title="Essenciais">
            <p className="card-subtitle hi-form-section-hint">
              Campos obrigatórios para criar o vínculo no RH.
            </p>
            <div className="hi-form-grid">
              <Field label="Nome completo" required className="hi-span-all">
                <input
                  value={form.nome}
                  onChange={(e) => set('nome', e.target.value)}
                  autoComplete="name"
                  required
                />
              </Field>
              <Field label="CPF" required hint="Máscara visual; só dígitos no envio">
                <input
                  value={form.cpf}
                  onChange={(e) => set('cpf', maskCpfInput(e.target.value))}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="000.000.000-00"
                  required
                />
              </Field>
              <Field label="Salário base" required>
                <input
                  value={form.salario_base}
                  onChange={(e) => set('salario_base', e.target.value)}
                  onBlur={() => {
                    if (!form.salario_base.trim()) return
                    const formatted = formatMoneyInput(form.salario_base)
                    if (formatted !== form.salario_base) set('salario_base', formatted)
                  }}
                  inputMode="decimal"
                  placeholder="0,00"
                  required
                />
              </Field>
              <Field label="Admissão" required>
                <input
                  type="date"
                  value={form.data_admissao}
                  onChange={(e) => set('data_admissao', e.target.value)}
                  required
                />
              </Field>
              <Field label="Matrícula">
                <input value={form.matricula} onChange={(e) => set('matricula', e.target.value)} />
              </Field>
              <Field label="Regime">
                <select value={form.regime} onChange={(e) => set('regime', e.target.value)}>
                  <option value="clt">CLT</option>
                  <option value="pj">PJ</option>
                  <option value="estagio">Estágio</option>
                  <option value="temporario">Temporário</option>
                  <option value="autonomo">Autônomo</option>
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => set('status', e.target.value)}>
                  <option value="ativo">Ativo</option>
                  <option value="ferias">Em férias</option>
                  <option value="afastado">Afastado</option>
                  <option value="desligado">Desligado</option>
                  <option value="admissao_pendente">Admissão pendente</option>
                </select>
              </Field>
              <Field label="Cargo">
                <select value={form.cargo} onChange={(e) => set('cargo', e.target.value)}>
                  <option value="">Sem cargo</option>
                  {(catalogs.data?.cargos || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Departamento">
                <select
                  value={form.departamento}
                  onChange={(e) => set('departamento', e.target.value)}
                >
                  <option value="">Sem departamento</option>
                  {(catalogs.data?.departamentos || []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.nome}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Carga horária" hint="Horas mensais">
                <input
                  value={form.carga_horaria}
                  onChange={(e) => set('carga_horaria', e.target.value)}
                  inputMode="numeric"
                />
              </Field>
            </div>
          </Section>
        </div>

        <div
          className="hi-form-panel"
          hidden={tab !== 'contato'}
          id="panel-contato"
          role="tabpanel"
        >
          <Section title="Contato">
            <p className="card-subtitle hi-form-section-hint">
              E-mail profissional e telefone vêm do Bitrix na sincronização; foto também é vinculada.
            </p>
            <div className="hi-form-grid">
              <Field label="E-mail profissional" className="hi-span-2">
                <input
                  type="email"
                  value={form.email_profissional}
                  onChange={(e) => set('email_profissional', e.target.value)}
                  autoComplete="email"
                />
              </Field>
              <Field label="E-mail corporativo" className="hi-span-2">
                <input
                  type="email"
                  value={form.email_corporativo}
                  onChange={(e) => set('email_corporativo', e.target.value)}
                  autoComplete="email"
                />
              </Field>
              <Field label="E-mail pessoal" className="hi-span-2">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  autoComplete="email"
                />
              </Field>
              <Field label="Telefone">
                <input
                  value={form.telefone}
                  onChange={(e) => set('telefone', maskPhoneInput(e.target.value))}
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="(00) 0000-0000"
                />
              </Field>
              <Field label="Celular">
                <input
                  value={form.celular}
                  onChange={(e) => set('celular', maskPhoneInput(e.target.value))}
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="(00) 00000-0000"
                />
              </Field>
            </div>
          </Section>
        </div>

        <div
          className="hi-form-panel"
          hidden={tab !== 'documentos'}
          id="panel-documentos"
          role="tabpanel"
        >
          <Section title="Documentos">
            <p className="card-subtitle hi-form-section-hint">
              Identidade civil e documentos trabalhistas.
            </p>
            <div className="hi-form-grid">
              <Field label="RG">
                <input value={form.rg} onChange={(e) => set('rg', e.target.value)} />
              </Field>
              <Field label="Nascimento">
                <input
                  type="date"
                  value={form.data_nascimento}
                  onChange={(e) => set('data_nascimento', e.target.value)}
                />
              </Field>
              <Field label="Sexo">
                <select value={form.sexo} onChange={(e) => set('sexo', e.target.value)}>
                  <option value="">—</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                  <option value="O">Outro</option>
                </select>
              </Field>
              <Field label="Estado civil">
                <select
                  value={form.estado_civil}
                  onChange={(e) => set('estado_civil', e.target.value)}
                >
                  <option value="">—</option>
                  <option value="solteiro">Solteiro(a)</option>
                  <option value="casado">Casado(a)</option>
                  <option value="divorc">Divorciado(a)</option>
                  <option value="viuvo">Viúvo(a)</option>
                  <option value="uniao">União estável</option>
                </select>
              </Field>
              <Field label="Nacionalidade" className="hi-span-2">
                <input
                  value={form.nacionalidade}
                  onChange={(e) => set('nacionalidade', e.target.value)}
                />
              </Field>
              <Field label="PIS/PASEP">
                <input value={form.pis_pasep} onChange={(e) => set('pis_pasep', e.target.value)} />
              </Field>
              <Field label="CTPS nº">
                <input
                  value={form.ctps_numero}
                  onChange={(e) => set('ctps_numero', e.target.value)}
                />
              </Field>
              <Field label="CTPS série">
                <input value={form.ctps_serie} onChange={(e) => set('ctps_serie', e.target.value)} />
              </Field>
              <Field label="CTPS UF">
                <input
                  value={form.ctps_uf}
                  onChange={(e) => set('ctps_uf', e.target.value)}
                  maxLength={2}
                />
              </Field>
            </div>
          </Section>
        </div>

        <div
          className="hi-form-panel"
          hidden={tab !== 'endereco'}
          id="panel-endereco"
          role="tabpanel"
        >
          <Section title="Endereço">
            <p className="card-subtitle hi-form-section-hint">
              Residência para documentação e benefícios.
            </p>
            {!showEndereco ? (
              <PanelEmpty
                message="Nenhum endereço informado."
                cta="Preencher endereço"
                onReveal={() => setEnderecoOpen(true)}
              />
            ) : (
              <div className="hi-form-grid">
                <Field label="CEP">
                  <input value={form.cep} onChange={(e) => set('cep', e.target.value)} />
                </Field>
                <Field label="Logradouro" className="hi-span-2">
                  <input
                    value={form.logradouro}
                    onChange={(e) => set('logradouro', e.target.value)}
                  />
                </Field>
                <Field label="Número">
                  <input value={form.numero} onChange={(e) => set('numero', e.target.value)} />
                </Field>
                <Field label="Complemento" className="hi-span-2">
                  <input
                    value={form.complemento}
                    onChange={(e) => set('complemento', e.target.value)}
                  />
                </Field>
                <Field label="Bairro">
                  <input value={form.bairro} onChange={(e) => set('bairro', e.target.value)} />
                </Field>
                <Field label="Cidade" className="hi-span-2">
                  <input value={form.cidade} onChange={(e) => set('cidade', e.target.value)} />
                </Field>
                <Field label="UF">
                  <input
                    value={form.estado}
                    onChange={(e) => set('estado', e.target.value)}
                    maxLength={2}
                  />
                </Field>
              </div>
            )}
          </Section>
        </div>

        <div
          className="hi-form-panel"
          hidden={tab !== 'bancario'}
          id="panel-bancario"
          role="tabpanel"
        >
          <Section title="Dados bancários">
            <p className="card-subtitle hi-form-section-hint">
              Conta e PIX para pagamentos e reembolsos.
            </p>
            {!showBancario ? (
              <PanelEmpty
                message="Nenhum dado bancário informado."
                cta="Preencher dados bancários"
                onReveal={() => setBancarioOpen(true)}
              />
            ) : (
              <div className="hi-form-grid">
                <Field label="Banco" className="hi-span-2">
                  <input value={form.banco} onChange={(e) => set('banco', e.target.value)} />
                </Field>
                <Field label="Agência">
                  <input value={form.agencia} onChange={(e) => set('agencia', e.target.value)} />
                </Field>
                <Field label="Conta">
                  <input value={form.conta} onChange={(e) => set('conta', e.target.value)} />
                </Field>
                <Field label="Tipo conta">
                  <select
                    value={form.tipo_conta}
                    onChange={(e) => set('tipo_conta', e.target.value)}
                  >
                    <option value="">—</option>
                    <option value="corrente">Corrente</option>
                    <option value="poupanca">Poupança</option>
                  </select>
                </Field>
                <Field label="PIX tipo">
                  <select value={form.pix_tipo} onChange={(e) => set('pix_tipo', e.target.value)}>
                    <option value="">—</option>
                    <option value="cpf">CPF</option>
                    <option value="cnpj">CNPJ</option>
                    <option value="email">E-mail</option>
                    <option value="telefone">Telefone</option>
                    <option value="aleatoria">Aleatória</option>
                  </select>
                </Field>
                <Field label="PIX chave" className="hi-span-2">
                  <input value={form.pix_chave} onChange={(e) => set('pix_chave', e.target.value)} />
                </Field>
              </div>
            )}
          </Section>
        </div>

        <div
          className="hi-form-panel"
          hidden={tab !== 'observacoes'}
          id="panel-observacoes"
          role="tabpanel"
        >
          <Section title="Observações">
            <p className="card-subtitle hi-form-section-hint">
              Notas internas visíveis apenas ao RH.
            </p>
            {!showObs ? (
              <div className="hi-form-obs-collapsed">
                <Muted>Nenhuma observação registrada.</Muted>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setObsOpen(true)}
                >
                  Adicionar nota
                </button>
              </div>
            ) : (
              <div className="hi-form-grid">
                <Field label="Observações" className="hi-span-all">
                  <textarea
                    rows={4}
                    value={form.observacoes}
                    onChange={(e) => set('observacoes', e.target.value)}
                    placeholder="Contexto de admissão, restrições, combinações com gestor…"
                  />
                </Field>
              </div>
            )}
          </Section>
        </div>

        <div className="hi-form-sticky" role="group" aria-label="Ações do formulário">
          <p className="hi-form-sticky-meta">
            {dirty ? 'Alterações não salvas' : 'Nenhuma alteração'}
          </p>
          <div className="hi-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={primaryDisabled}>
              {save.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Cadastrar'}
            </button>
          </div>
        </div>
      </form>
    </section>
  )
}

export default ColaboradorFormPage
